// Phase 3a/3b: parcel detail page view. Reads the parcel UID from a
// `data-parcel-uid` attribute on the rendered root, then hydrates:
//
// - Header actions (Phase 3b): primary CTA stays SSR; "In a project"
//   and "Pin to a project" dropdown pickers are appended client-side
//   because they need the user's project list, which is per-user
//   data we don't want to bake into the SSR template.
// - "Your scenarios on this parcel" (Phase 3a): grouped by home
//   project + a standalone bucket; cross-project rows link to their
//   real home URL.
// - "Pinned in" (Phase 3b): passive list of projects this user owns
//   that workspace-pin this parcel.

import {getLogger} from '../../logging';
import {svc} from '../../request';
import {renderDealBlock} from '../../ui/dealblock';

const logger = getLogger('parceldetailview');

interface IProjectChoice {
	readonly slug: string;
	readonly title: string;
}

type PickerMode = 'in-project' | 'pin-to-project';

export class ParcelDetailPageView {
	private rootEl: HTMLElement | null;
	private scenariosHostEl: HTMLElement | null;
	private pinsHostEl: HTMLElement | null;
	private actionsEl: HTMLElement | null;
	private parcelUid: string | null = null;
	// Per-user project list. Lazy-loaded on first picker open so the
	// page doesn't fetch it for users who only ever click the primary
	// CTA. Null until first attempt; empty array on success-with-no-
	// projects (so we distinguish "haven't loaded" from "no projects
	// exist for this user").
	private projects: ReadonlyArray<IProjectChoice> | null = null;
	private projectsLoading: Promise<void> | null = null;
	// Tracks the currently-open picker DOM node so a second click on
	// the trigger toggles it closed and outside-clicks dismiss it.
	private openPicker: HTMLElement | null = null;
	private outsideClickHandler: ((evt: MouseEvent) => void) | null = null;

	constructor() {
		this.rootEl = document.getElementById('id_lb-parcel-detail');
		this.scenariosHostEl = document.getElementById('id_lb-parcel-detail-scenarios-host');
		this.pinsHostEl = document.getElementById('id_lb-parcel-detail-pins-host');
		this.actionsEl = document.getElementById('id_lb-parcel-detail-actions');
		const uid = this.rootEl && this.rootEl.getAttribute('data-parcel-uid');
		this.parcelUid = uid && uid.trim().length > 0 ? uid : null;
		this.appendPhase3bActions();
		void this.hydrateScenarios();
		void this.hydratePins();
	}

	// ─────────── Header actions (Phase 3b) ───────────

	private appendPhase3bActions(): void {
		if (!this.actionsEl) {
			return;
		}
		// Label note: PR #71 shipped "In a project ▾" / "Pin to a
		// project ▾" which read as states rather than actions and
		// confused users on first visit. PR #72 parallels the primary
		// CTA's "Run Deal Calc on this parcel" verb so the three
		// buttons read as variants of the same action surface:
		//   1. "Run Deal Calc on this parcel"          (parcel-only)
		//   2. "Run Deal Calc in a project ▾"          (project-attached)
		//   3. "Pin parcel to a project ▾"             (workspace pin)
		const inProject = this.makeActionButton(
			'Run Deal Calc in a project ▾',
			'Run Deal Calc in a project',
		);
		inProject.addEventListener('click', evt => {
			evt.stopPropagation();
			this.togglePicker(inProject, 'in-project');
		});
		this.actionsEl.appendChild(inProject);

		const pinTo = this.makeActionButton(
			'Pin parcel to a project ▾',
			'Pin parcel to a project',
		);
		pinTo.addEventListener('click', evt => {
			evt.stopPropagation();
			this.togglePicker(pinTo, 'pin-to-project');
		});
		this.actionsEl.appendChild(pinTo);
	}

	// Reuses the existing `workspace-card__button` pattern (pill with
	// soft white fill + hairline border, hover lifts to opaque white +
	// ring border, primary-text on hover). PR #71's custom mdc-button-
	// based styling lost a specificity fight with the global mdc rules
	// and rendered as a dark filled pill whose hover state blanked the
	// text. `workspace-card__button` is the established secondary-pill
	// pattern in this app — no new SCSS, no broken states.
	private makeActionButton(label: string, srLabel: string): HTMLButtonElement {
		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'workspace-card__button lb-parcel-detail__action-button';
		btn.setAttribute('aria-label', srLabel);
		btn.textContent = label;
		return btn;
	}

	private togglePicker(trigger: HTMLButtonElement, mode: PickerMode): void {
		// Toggle closed if the SAME picker is already open on this trigger.
		if (this.openPicker && this.openPicker.dataset['triggerMode'] === mode) {
			this.closePicker();
			return;
		}
		this.closePicker();
		const picker = document.createElement('div');
		picker.className = 'lb-parcel-detail__picker';
		picker.dataset['triggerMode'] = mode;
		const loading = document.createElement('div');
		loading.className = 'lb-parcel-detail__picker-loading';
		loading.textContent = 'Loading your projects…';
		picker.appendChild(loading);
		// Anchor the picker to the trigger by inserting as a sibling
		// and positioning via CSS (absolute, below the trigger).
		trigger.insertAdjacentElement('afterend', picker);
		this.openPicker = picker;
		// Outside-click dismissal. Captured on the next tick so the
		// initiating click doesn't immediately re-fire and close us.
		window.setTimeout(() => {
			this.outsideClickHandler = (evt: MouseEvent) => {
				if (this.openPicker
					&& !this.openPicker.contains(evt.target as Node)
					&& !trigger.contains(evt.target as Node)) {
					this.closePicker();
				}
			};
			document.addEventListener('click', this.outsideClickHandler);
		}, 0);
		void this.populatePicker(picker, mode, trigger);
	}

	private closePicker(): void {
		if (this.openPicker && this.openPicker.parentNode) {
			this.openPicker.parentNode.removeChild(this.openPicker);
		}
		this.openPicker = null;
		if (this.outsideClickHandler) {
			document.removeEventListener('click', this.outsideClickHandler);
			this.outsideClickHandler = null;
		}
	}

	private async ensureProjects(): Promise<void> {
		if (this.projects !== null) {
			return;
		}
		if (this.projectsLoading) {
			return this.projectsLoading;
		}
		this.projectsLoading = (async () => {
			try {
				const rows = await svc.project.list();
				this.projects = rows
					.filter(p => !p.archived)
					.map(p => ({slug: p.slug, title: p.title || p.slug}));
			} catch (exc) {
				logger.error('ensureProjects: failed to load project list', exc);
				this.projects = [];
			}
		})();
		return this.projectsLoading;
	}

	private async populatePicker(picker: HTMLElement, mode: PickerMode, trigger: HTMLButtonElement): Promise<void> {
		await this.ensureProjects();
		// Re-check open state — outside-click or another trigger may
		// have closed/replaced us during the await.
		if (this.openPicker !== picker || !picker.parentNode) {
			return;
		}
		while (picker.firstChild) {
			picker.removeChild(picker.firstChild);
		}
		const all = this.projects || [];
		// For pin-to-project, hide projects already pinning this parcel
		// so the menu doesn't offer a no-op.
		let choices = all;
		if (mode === 'pin-to-project') {
			const pinned = await this.fetchPinnedSlugs();
			if (this.openPicker !== picker || !picker.parentNode) {
				return;
			}
			choices = all.filter(p => !pinned.has(p.slug));
		}
		if (choices.length === 0) {
			const empty = document.createElement('div');
			empty.className = 'lb-parcel-detail__picker-empty';
			empty.textContent = mode === 'pin-to-project'
				? 'This parcel is already pinned in every one of your projects.'
				: 'You don’t have any projects yet.';
			picker.appendChild(empty);
			return;
		}
		const list = document.createElement('ul');
		list.className = 'lb-parcel-detail__picker-list';
		choices.forEach(project => {
			const li = document.createElement('li');
			li.className = 'lb-parcel-detail__picker-item';
			if (mode === 'in-project') {
				const a = document.createElement('a');
				a.className = 'lb-parcel-detail__picker-action';
				a.href = `/${encodeURIComponent(project.slug)}/deals/new/?parcel=${encodeURIComponent(this.parcelUid || '')}`;
				a.textContent = project.title;
				li.appendChild(a);
			} else {
				const b = document.createElement('button');
				b.type = 'button';
				b.className = 'lb-parcel-detail__picker-action';
				b.textContent = project.title;
				b.addEventListener('click', evt => {
					evt.preventDefault();
					evt.stopPropagation();
					this.pinToProject(project, b, trigger)
						.catch(exc => logger.error('pinToProject failed', exc));
				});
				li.appendChild(b);
			}
			list.appendChild(li);
		});
		picker.appendChild(list);
	}

	private async fetchPinnedSlugs(): Promise<Set<string>> {
		if (!this.parcelUid) {
			return new Set();
		}
		try {
			const rv = await svc.parcel.pins(this.parcelUid);
			return new Set((rv.pinnedIn || []).map(p => p.slug));
		} catch {
			return new Set();
		}
	}

	private async pinToProject(project: IProjectChoice, btn: HTMLButtonElement, _trigger: HTMLButtonElement): Promise<void> {
		if (!this.parcelUid) {
			return;
		}
		btn.disabled = true;
		const origText = btn.textContent;
		btn.textContent = 'Pinning…';
		try {
			await svc.workspaceItem.create(project.slug, {
				itemType: 'parcel',
				targetKey: this.parcelUid,
				label: '',
				source: 'parcel_detail_page',
				payloadJson: {},
			});
		} catch (exc) {
			btn.disabled = false;
			btn.textContent = origText;
			throw exc;
		}
		this.closePicker();
		// Refresh the passive "Pinned in" section so the new pin
		// shows up without a hard reload. Also clear cached
		// projects list so a re-open of the pin picker reflects the
		// new pinned-slugs exclusion.
		await this.hydratePins();
	}

	// ─────────── Scenarios section (Phase 3a) ───────────

	private async hydrateScenarios(): Promise<void> {
		if (!this.parcelUid || !this.scenariosHostEl) {
			return;
		}
		try {
			const rollup = await svc.parcel.deals(this.parcelUid);
			this.renderScenarios(rollup);
		} catch (exc) {
			logger.error('hydrateScenarios: failed', exc);
			this.renderHostError(this.scenariosHostEl, 'Couldn’t load scenarios.');
		}
	}

	private renderScenarios(rollup: IParcelDealsResponse): void {
		if (!this.scenariosHostEl) {
			return;
		}
		while (this.scenariosHostEl.firstChild) {
			this.scenariosHostEl.removeChild(this.scenariosHostEl.firstChild);
		}
		const projectSlugs = Object.keys(rollup.byProject || {});
		const standaloneCount = (rollup.standalone || []).length;
		if (projectSlugs.length === 0 && standaloneCount === 0) {
			const empty = document.createElement('div');
			empty.className = 'lb-parcel-detail__empty';
			const strong = document.createElement('strong');
			strong.textContent = 'No underwriting yet on this parcel.';
			const sub = document.createElement('span');
			sub.textContent = 'Click "Run Deal Calc on this parcel" above to start a scenario.';
			empty.appendChild(strong);
			empty.appendChild(sub);
			this.scenariosHostEl.appendChild(empty);
			return;
		}
		projectSlugs.sort();
		projectSlugs.forEach(projectSlug => {
			const deals = rollup.byProject[projectSlug] || [];
			this.scenariosHostEl!.appendChild(this.renderGroup(
				`In ${projectSlug}`,
				deals,
				deal => `/${encodeURIComponent(projectSlug)}/deals/${encodeURIComponent(deal.slug)}/`,
			));
		});
		if (standaloneCount > 0) {
			this.scenariosHostEl.appendChild(this.renderGroup(
				'Standalone',
				rollup.standalone,
				deal => `/deals/${encodeURIComponent(deal.slug)}/`,
			));
		}
	}

	private renderGroup(
		heading: string,
		deals: ReadonlyArray<IParcelAttachedDealRef>,
		hrefFor: (deal: IParcelAttachedDealRef) => string,
	): HTMLElement {
		const group = document.createElement('div');
		group.className = 'lb-parcel-detail__group';
		const h = document.createElement('h3');
		h.className = 'lb-parcel-detail__group-heading';
		h.textContent = heading;
		group.appendChild(h);
		const list = document.createElement('ul');
		list.className = 'lb-parcel-detail__scenario-list';
		// Deal-forward PR B: each scenario row is the shared DealBlock
		// component (`variant: 'row'`). These rollup refs carry only
		// slug/title (no `state_json`), so no mode token renders; the
		// group's href rule decides project-scoped vs flat links.
		deals.forEach(deal => {
			list.appendChild(renderDealBlock(
				{slug: deal.slug, title: deal.title},
				{variant: 'row', href: hrefFor(deal)},
			));
		});
		group.appendChild(list);
		return group;
	}

	// ─────────── Pinned-in section (Phase 3b) ───────────

	private async hydratePins(): Promise<void> {
		if (!this.parcelUid || !this.pinsHostEl) {
			return;
		}
		try {
			const rv = await svc.parcel.pins(this.parcelUid);
			this.renderPins(rv.pinnedIn || []);
		} catch (exc) {
			logger.error('hydratePins: failed', exc);
			this.renderHostError(this.pinsHostEl, 'Couldn’t load pin status.');
		}
	}

	private renderPins(pinnedIn: ReadonlyArray<IParcelPinRef>): void {
		if (!this.pinsHostEl) {
			return;
		}
		while (this.pinsHostEl.firstChild) {
			this.pinsHostEl.removeChild(this.pinsHostEl.firstChild);
		}
		if (pinnedIn.length === 0) {
			const empty = document.createElement('div');
			empty.className = 'lb-parcel-detail__empty';
			const strong = document.createElement('strong');
			strong.textContent = 'Not pinned anywhere yet.';
			const sub = document.createElement('span');
			sub.textContent = 'Use "Pin to a project" above to add this parcel to a project’s workspace.';
			empty.appendChild(strong);
			empty.appendChild(sub);
			this.pinsHostEl.appendChild(empty);
			return;
		}
		const list = document.createElement('ul');
		list.className = 'lb-parcel-detail__pins-list';
		pinnedIn.forEach(project => {
			const li = document.createElement('li');
			li.className = 'lb-parcel-detail__pins-item';
			const a = document.createElement('a');
			a.className = 'lb-parcel-detail__pins-link';
			a.href = `/${encodeURIComponent(project.slug)}/`;
			a.textContent = project.title;
			const slug = document.createElement('span');
			slug.className = 'lb-parcel-detail__pins-slug';
			slug.textContent = project.slug;
			li.appendChild(a);
			li.appendChild(slug);
			list.appendChild(li);
		});
		this.pinsHostEl.appendChild(list);
	}

	// ─────────── Shared ───────────

	private renderHostError(host: HTMLElement, msg: string): void {
		while (host.firstChild) {
			host.removeChild(host.firstChild);
		}
		const err = document.createElement('div');
		err.className = 'lb-parcel-detail__empty';
		const strong = document.createElement('strong');
		strong.textContent = msg;
		const sub = document.createElement('span');
		sub.textContent = 'Try refreshing the page.';
		err.appendChild(strong);
		err.appendChild(sub);
		host.appendChild(err);
	}
}
